import React, { useEffect, useRef, useState } from "react";
import { X, Tag, Layers, Info, Heart } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useWishlist } from "../../hooks/useWishlist";
import toast from "react-hot-toast";
import { API_BASE_URL } from "../../config/api";

function sanitizeDescription(desc) {
  if (!desc) return "";
  let clean = desc.replace(/\\n/g, "\n");
  clean = clean.replace(/\n{2,}/g, "\n");
  clean = clean.replace(/\n/g, "<br>");
  clean = clean.replace(/^(<br>)+/, "").replace(/(<br>)+$/, "");
  return clean.trim();
}

const QuickViewModal = ({ open, onClose, product }) => {
  const { user } = useAuth();
  const {
    toggleWishlist,
    isInWishlist,
    loading: wishlistLoading,
  } = useWishlist();
  const modalRef = useRef(null);
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
  const [imageAnimKey, setImageAnimKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    setSelectedVariantIdx(0);
    setImageAnimKey(0);
  }, [open, product]);

  useEffect(() => {
    if (!open) return;
    const focusableSelectors =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const modal = modalRef.current;
    if (!modal) return;
    const focusableEls = modal.querySelectorAll(focusableSelectors);
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];
    if (firstEl) firstEl.focus();
    function trap(e) {
      if (e.key !== "Tab") return;
      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }
    modal.addEventListener("keydown", trap);
    return () => modal.removeEventListener("keydown", trap);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  if (!open || !product) return null;

  // Helper function to get proper image URL (same as ProductDetailPage)
  const getImageUrl = (imageUrl) => {
    if (!imageUrl)
      return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='20' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";

    if (imageUrl.startsWith("http")) return imageUrl;

    const base = (API_BASE_URL || "").replace(/\/+$/, "");
    let cleanUrl = imageUrl
      .replace(/^\/uploads\//, "")
      .replace(/^uploads\//, "");
    return `${base}/uploads/${cleanUrl}`;
  };

  // Get current images (same logic as ProductDetailPage)
  const getCurrentImages = () => {
    if (!product) return [];
    if (selectedVariant?.images?.length > 0) return selectedVariant.images;
    return product.images || [];
  };

  // Images and variants
  const variants = product.variants || [];
  const variantsWithImages = variants.filter(
    (v) => v.images && v.images.length > 0
  );
  const topVariants = variantsWithImages.slice(0, 10);
  const selectedVariant =
    topVariants[selectedVariantIdx] || variantsWithImages[0] || variants[0];
  const currentImages = getCurrentImages();
  const mainImage = getImageUrl(currentImages[0]?.url);

  // Price logic
  const currentPrice =
    selectedVariant?.price_sale ||
    selectedVariant?.price_regular ||
    product.min_price;
  const originalPrice = selectedVariant?.price_regular || product.max_price;
  const hasDiscount =
    selectedVariant?.price_sale &&
    selectedVariant.price_sale < selectedVariant.price_regular;
  const discountPercentage = hasDiscount
    ? Math.round(
        (1 - selectedVariant.price_sale / selectedVariant.price_regular) * 100
      )
    : 0;

  // Categories and tags
  const categories = (product.categories || [])
    .map((c) => c?.category)
    .filter(Boolean);
  const tags = (product.tags || []).map((t) => t?.tag).filter(Boolean);

  // Details/attributes
  let dimensionRows = [];
  if (
    selectedVariant &&
    selectedVariant.attributes &&
    selectedVariant.attributes.length > 0
  ) {
    dimensionRows = selectedVariant.attributes.map((attr) => ({
      key: attr.name,
      value: attr.value,
    }));
  } else if (product.description) {
    const lines = product.description.split(/\n|<br\s*\/?\s*>/i);
    dimensionRows = lines
      .map((line) => {
        const match = line.match(/^(\w[\w\s]+):\s*(.+)$/);
        if (match) return { key: match[1].trim(), value: match[2].trim() };
        return null;
      })
      .filter(Boolean);
  }

  const cleanDescription = sanitizeDescription(product.description);

  // Animation for variant image change
  const handleVariantClick = (idx) => {
    setImageAnimKey((prev) => prev + 1); // triggers fade/scale animation
    setSelectedVariantIdx(idx);
  };

  const handleToggleWishlist = async () => {
    if (!user) {
      toast.error("Please log in to manage wishlist");
      return;
    }

    try {
      await toggleWishlist(product.id);
      const isInWishlistItem = isInWishlist(product.id);
      toast.success(
        isInWishlistItem
          ? `${product.name} removed from wishlist!`
          : `${product.name} added to wishlist!`
      );
    } catch (error) {
      console.error("Failed to toggle wishlist:", error);
      toast.error(error.message || "Failed to update wishlist");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center font-sans p-4">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 transition-opacity duration-300"
        onClick={onClose}
      />
      {/* Modal Panel */}
      <div
        ref={modalRef}
        className={`relative bg-white transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] transform flex flex-col font-sans p-4
        ${
          open
            ? "translate-y-0 opacity-100 scale-100"
            : "translate-y-4 opacity-0 scale-95"
        }`}
        style={{
          outline: "none",
          width: "min(95vw, 800px)",
          maxWidth: "800px",
          maxHeight: "90vh",
          height: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button - Top Right */}
        <button
          className="absolute top-4 right-4 z-30 p-2 hover:bg-gray-100 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
          style={{ borderRadius: 0 }}
          onClick={onClose}
          aria-label="Close"
        >
          <X className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>

        {/* Sticky Header */}
        <div
          className="sticky top-0 z-20 bg-white flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 gap-3 border-b border-gray-100"
          style={{ borderRadius: 0 }}
        >
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <h2
              className="text-lg sm:text-xl md:text-2xl font-bold text-secondary-900 break-words leading-tight"
              style={{ fontFamily: "inherit" }}
            >
              {product.name}
            </h2>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              {hasDiscount ? (
                <div className="flex flex-col">
                  <span className="text-sm sm:text-base text-gray-400 line-through">
                    MRP ₹{originalPrice?.toLocaleString()}
                  </span>
                  <span className="text-lg sm:text-xl font-semibold text-primary-600">
                    ₹{currentPrice?.toLocaleString()}
                  </span>
                </div>
              ) : (
                <span className="text-lg sm:text-xl font-semibold text-primary-600">
                  MRP ₹{currentPrice?.toLocaleString()}
                </span>
              )}
              {hasDiscount && (
                <span
                  className="px-2 py-1 text-xs bg-red-100 text-red-600 font-medium"
                  style={{ borderRadius: 0 }}
                >
                  -{discountPercentage}%
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleToggleWishlist}
              disabled={wishlistLoading}
              className="p-2 hover:bg-gray-100 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
              style={{ borderRadius: 0 }}
              title={
                isInWishlist(product.id)
                  ? "Remove from Wishlist"
                  : "Add to Wishlist"
              }
            >
              <Heart
                className={`w-5 h-5 sm:w-6 sm:h-6 transition-colors ${
                  isInWishlist(product.id)
                    ? "text-red-500 fill-current"
                    : "text-gray-400 hover:text-red-500"
                }`}
              />
            </button>
            <a
              href={`/products/${product.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-3 sm:px-4 py-2 bg-primary-600 text-white text-sm sm:text-base font-semibold hover:bg-primary-700 transition whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              style={{ borderRadius: 0 }}
            >
              View Full
            </a>
          </div>
        </div>
        {/* Main Content Scrollable */}
        <div
          className="flex-1 overflow-y-auto px-4 sm:px-6 md:px-8 py-4 sm:py-6 flex flex-col gap-4 sm:gap-6"
          style={{ background: "#fff", borderRadius: 0 }}
        >
          {/* Main Image */}
          <div
            className="flex flex-col items-center"
            style={{ borderRadius: 0 }}
          >
            <div
              key={imageAnimKey + "-" + selectedVariantIdx}
              className="w-full max-w-sm sm:max-w-md h-48 sm:h-64 md:h-72 flex items-center justify-center bg-gray-50 mb-3 transition-all duration-300 ease-in-out animate-fadeInScale"
              style={{ borderRadius: 0 }}
            >
              {currentImages.length > 0 ? (
                <img
                  src={getImageUrl(currentImages[0]?.url)}
                  alt={currentImages[0]?.alt_text || product.name}
                  loading="eager"
                  className="w-full h-48 sm:h-64 md:h-72 object-contain"
                  style={{ borderRadius: 0, transition: "opacity 0.2s" }}
                  onError={(e) => {
                    e.target.src =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='20' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
                  }}
                />
              ) : (
                <div className="w-full h-48 sm:h-64 md:h-72 flex items-center justify-center">
                  <span className="text-gray-400">No image available</span>
                </div>
              )}
            </div>
            {/* Variant Gallery */}
            {topVariants.length > 0 && (
              <div
                className="w-full flex overflow-x-auto gap-2 sm:gap-3 py-3 mt-2 scrollbar-hide px-4"
                style={{ borderRadius: 0 }}
              >
                {topVariants.map((variant, idx) => {
                  const variantImages = variant.images || [];
                  return (
                    <button
                      key={variant.id}
                      onClick={() => handleVariantClick(idx)}
                      className={`flex flex-col items-center min-w-[50px] sm:min-w-[60px] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 flex-shrink-0 ${
                        selectedVariantIdx === idx
                          ? "scale-105 ring-2 ring-black"
                          : "opacity-80 hover:opacity-100 hover:scale-105"
                      }`}
                      style={{ borderRadius: 0, background: "#fff" }}
                    >
                      {variantImages.length > 0 ? (
                        <img
                          src={getImageUrl(variantImages[0]?.url)}
                          alt={
                            variantImages[0]?.alt_text ||
                            variant.name ||
                            variant.sku ||
                            "Variant"
                          }
                          className="w-10 h-10 sm:w-12 sm:h-12 object-cover mb-1"
                          style={{ borderRadius: 0 }}
                          onError={(e) => {
                            e.target.src =
                              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
                          }}
                        />
                      ) : (
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 flex items-center justify-center mb-1">
                          <span className="text-gray-400 text-xs">
                            No Image
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {/* Description & Details */}
          <div
            className="flex flex-col lg:flex-row gap-4 sm:gap-6"
            style={{ borderRadius: 0 }}
          >
            {/* Description */}
            <div
              className="flex-1 bg-gray-50 p-4 sm:p-5"
              style={{ borderRadius: 0 }}
            >
              <div
                className="flex items-center gap-2 mb-3 text-primary-700 font-semibold text-base sm:text-lg"
                style={{ fontFamily: "inherit" }}
              >
                <Info className="w-4 h-4 sm:w-5 sm:h-5" /> Description
              </div>
              {cleanDescription && (
                <div
                  className="prose prose-sm max-w-none text-gray-800 text-sm sm:text-base leading-relaxed"
                  style={{ fontFamily: "inherit" }}
                  dangerouslySetInnerHTML={{ __html: cleanDescription }}
                />
              )}
            </div>
            {/* Product Details */}
            {dimensionRows.length > 0 && (
              <div
                className="flex-1 bg-gray-50 p-4 sm:p-5 min-w-0"
                style={{ borderRadius: 0 }}
              >
                <div
                  className="flex items-center gap-2 mb-3 text-primary-700 font-semibold text-base sm:text-lg"
                  style={{ fontFamily: "inherit" }}
                >
                  <Layers className="w-4 h-4 sm:w-5 sm:h-5" /> Product Details
                </div>
                <div className="overflow-x-auto">
                  <table
                    className="w-full text-xs sm:text-sm"
                    style={{ fontFamily: "inherit" }}
                  >
                    <tbody>
                      {dimensionRows.map((row, idx) => (
                        <tr
                          key={idx}
                          className="border-b border-gray-200 last:border-b-0 hover:bg-gray-100 transition-colors"
                        >
                          <td className="py-2 px-2 font-medium text-gray-700 whitespace-nowrap">
                            {row.key}
                          </td>
                          <td className="py-2 px-2 text-gray-600 break-words">
                            {row.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          {/* Categories & Tags */}
          <div
            className="flex flex-col lg:flex-row gap-4 sm:gap-6"
            style={{ borderRadius: 0 }}
          >
            {categories.length > 0 && (
              <div
                className="flex-1 bg-gray-50 p-4 sm:p-5"
                style={{ borderRadius: 0 }}
              >
                <div
                  className="flex items-center gap-2 mb-3 text-primary-700 font-semibold text-base sm:text-lg"
                  style={{ fontFamily: "inherit" }}
                >
                  <Tag className="w-4 h-4 sm:w-5 sm:h-5" /> Categories
                </div>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <span
                      key={cat.id}
                      className="text-xs bg-primary-100 text-primary-800 px-2 py-1 font-medium hover:bg-primary-200 transition-colors"
                      style={{ borderRadius: 0, fontFamily: "inherit" }}
                    >
                      {cat.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {tags.length > 0 && (
              <div
                className="flex-1 bg-gray-50 p-4 sm:p-5"
                style={{ borderRadius: 0 }}
              >
                <div
                  className="flex items-center gap-2 mb-3 text-primary-700 font-semibold text-base sm:text-lg"
                  style={{ fontFamily: "inherit" }}
                >
                  <Tag className="w-4 h-4 sm:w-5 sm:h-5" /> Tags
                </div>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="text-xs px-2 py-1 font-medium hover:opacity-80 transition-opacity"
                      style={{
                        backgroundColor: tag.color || "#f3f4f6",
                        color: tag.color ? "#fff" : "#374151",
                        borderRadius: 0,
                        fontFamily: "inherit",
                      }}
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Animations */}
      <style>{`
        .animate-fadeInScale {
          animation: fadeInScale 0.35s cubic-bezier(0.4,0,0.2,1);
        }
        @keyframes fadeInScale {
          0% { opacity: 0; transform: scale(0.96); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default QuickViewModal;
